import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService } from '../../services/dataService';
import { Send } from 'lucide-react';

interface SupportMessage {
  id: string;
  sender: 'user' | 'admin';
  content: string;
  timestamp: any;
  read: boolean;
}

const SupportChat: React.FC = () => {
  const { tenantData, userData } = useAuth();
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load messages from Firestore
  useEffect(() => {
    if (!tenantData?.id) return;
    const unsubscribe = dataService.subscribeToSupportChat(tenantData.id, (msgs: SupportMessage[]) => {
      setMessages(msgs.sort((a, b) => a.timestamp?.seconds - b.timestamp?.seconds));
      setLoading(false);
    });
    return unsubscribe;
  }, [tenantData?.id]);

  // Mark support notifications as read when support chat is opened
  useEffect(() => {
    if (!tenantData?.id) return;
    
    const markSupportNotificationsAsRead = async () => {
      try {
        await dataService.markNotificationsAsReadByType(tenantData.id, 'support');
      } catch (error) {
        console.error('Error marking support notifications as read:', error);
      }
    };

    markSupportNotificationsAsRead();
  }, [tenantData?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!input.trim() || !tenantData?.id || !userData) return;
    setSending(true);
    try {
      await dataService.sendSupportMessage(tenantData.id, {
        sender: 'user',
        content: input.trim(),
        timestamp: new Date(),
        read: false,
        userId: userData.id,
        userName: userData.firstName + ' ' + userData.lastName
      });
      setInput('');
    } catch (err) {
      alert('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 flex flex-col h-[70vh]">
      <div className="p-4 border-b border-gray-100 dark:border-gray-700 text-lg font-semibold text-gray-900 dark:text-white">Support Chat</div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
        {loading ? (
          <div className="text-center text-gray-400 dark:text-gray-500">Loading...</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-400 dark:text-gray-500">No messages yet. Start the conversation!</div>
        ) : (
          messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`rounded-lg px-4 py-2 max-w-xs break-words shadow-sm ${
                msg.sender === 'user' 
                  ? 'bg-primary-100 dark:bg-primary-900 text-primary-900 dark:text-primary-100' 
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
              }`}>
                <div>{msg.content}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">{msg.timestamp?.toDate ? msg.timestamp.toDate().toLocaleString() : new Date(msg.timestamp).toLocaleString()}</div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSend} className="p-4 border-t border-gray-100 dark:border-gray-700 flex gap-2">
        <input
          type="text"
          className="flex-1 input dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
          placeholder="Type your message..."
          value={input}
          onChange={e => setInput(e.target.value)}
          disabled={sending}
        />
        <button type="submit" className="btn btn-primary flex items-center" disabled={sending || !input.trim()}>
          <Send className="h-5 w-5 mr-1" /> Send
        </button>
      </form>
    </div>
  );
};

export default SupportChat; 